/**
 * One slide, laid out.
 *
 * The first version stacked every element in one centred column whatever the slide was for,
 * which is why they all looked the same and why a three-bullet slide left two thirds of the
 * frame empty. A slide now has a *layout*, chosen by the model or inferred from what is on
 * it, and each one fills the frame differently:
 *
 *   statement  one idea at display size, and nothing else
 *   visual     the drawing takes the slide; the title gets out of the way
 *   split      prose against the visual, side by side
 *   grid       a set of short items as cards, because six bullets is a list nobody reads
 *   steps      an ordered build, numbered
 *   compare    two columns, headed
 *   question   a question, centred, for the check
 *
 * `cqw` units throughout, against a container query on the frame, so a slide composes the
 * same on a laptop preview as on the projector rather than being tuned for one of them.
 */

import type { CSSProperties, ReactNode } from 'react';
import { SlideElementView } from './SlideElementView';
import type { Slide, SlideElement } from './types';
import { TYPE, SPACE, DECK, inferLayout } from './design';
import type { SlideLayoutName as SlideLayout } from './types';
import { Icon, iconFor, iconsForSet } from './icons';
import { Motif, motifFor } from './motifs';

const isProse = (e: SlideElement): e is Extract<SlideElement, { kind: 'prose' }> => e.kind === 'prose';

/** Words too common to signal that a heading is restating its title. */
const STOP = new Set(['what', 'this', 'that', 'with', 'from', 'into', 'their', 'they', 'about', 'these', 'those', 'your']);

const titleStyle: CSSProperties = {
  fontSize: TYPE.title,
  lineHeight: 1.08,
  letterSpacing: '-.022em',
  fontWeight: 640,
  margin: 0,
  textWrap: 'balance',
};

/**
 * The masthead every slide wears: an eyebrow, the title, and a short brand rule.
 *
 * All three, on every layout. The first version put the rule on four layouts out of seven and
 * left the other three bare, which is not an identity — it is a decoration that shows up
 * sometimes. A deck is recognisable because every slide carries the same marks.
 *
 * The eyebrow does the work a title cannot: it says where in the hour this is, so a class
 * following along knows whether they are still on the same idea.
 */
function Masthead({ eyebrow, title, small }: { eyebrow?: string; title: ReactNode; small?: boolean }) {
  return (
    <div>
      {eyebrow ? (
        <div
          style={{
            fontSize: TYPE.caption,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            fontWeight: 680,
            color: DECK.inkSoft,
            marginBottom: 'clamp(.3rem, .7cqw, .6rem)',
          }}
        >
          {eyebrow}
        </div>
      ) : null}
      <h2 style={small ? { ...titleStyle, fontSize: TYPE.lead } : titleStyle}>{title}</h2>
      <div
        style={{
          width: 'clamp(2.4rem, 5cqw, 4.2rem)',
          height: '5px',
          borderRadius: '3px',
          background: 'var(--c-brand, #5b46d6)',
          marginTop: 'clamp(.5rem, 1.1cqw, .9rem)',
        }}
      />
    </div>
  );
}

const bulletStyle: CSSProperties = { fontSize: TYPE.body, lineHeight: 1.4 };

function Bullets({ items }: { items: string[] }) {
  return (
    <ul style={{ margin: 0, paddingLeft: '1.1em', display: 'grid', gap: SPACE.tight }}>
      {items.map((b, i) => <li key={i} style={bulletStyle}>{b}</li>)}
    </ul>
  );
}

/** A set of short items, as cards. Six bullets in a column is a list; six cards is a set. */
function CardGrid({ items }: { items: string[] }) {
  const cols = items.length <= 4 ? 2 : 3;
  // All of them or none of them — see `iconsForSet`.
  const icons = iconsForSet(items);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: SPACE.gap, width: '100%' }}>
      {items.map((b, i) => {
        const icon = icons?.[i] ?? null;
        return (
        <div
          key={i}
          style={{
            padding: 'clamp(.9rem, 1.8cqw, 1.6rem)',
            borderRadius: '14px',
            background: DECK.panel,
            border: `1px solid ${DECK.panelEdge}`,
            // The accent is an absolutely-positioned bar inside an overflow-hidden card, so
            // the corner radius clips it. A border, an inset shadow and a background band all
            // draw straight across the curve and overhang it.
            position: 'relative',
            overflow: 'hidden',
            fontSize: TYPE.body,
            lineHeight: 1.3,
            display: 'flex',
            alignItems: 'center',
            minHeight: '4.5cqw',
            gap: 'clamp(.6rem, 1.2cqw, 1rem)',
          }}
        >
          <span style={{ position: 'absolute', insetInline: 0, top: 0, height: '4px', background: 'var(--c-brand, #5b46d6)' }} />
          {icon ? (
            <span style={{ flex: 'none', color: 'var(--c-brand, #5b46d6)', display: 'flex' }}>
              <Icon name={icon} size={28} />
            </span>
          ) : null}
          <span>{b}</span>
        </div>
        );
      })}
    </div>
  );
}

/** An ordered build. The number does the work a bullet cannot: it says there is a sequence. */
function Steps({ items }: { items: string[] }) {
  return (
    <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: SPACE.gap, width: '100%' }}>
      {items.map((b, i) => (
        <li key={i} style={{ display: 'flex', gap: 'clamp(.8rem, 1.6cqw, 1.4rem)', alignItems: 'baseline' }}>
          <span
            style={{
              flex: 'none', fontSize: TYPE.lead, fontWeight: 700, lineHeight: 1,
              color: 'var(--c-brand, #5b46d6)', fontVariantNumeric: 'tabular-nums',
              minWidth: '1.6em',
            }}
          >
            {i + 1}
          </span>
          <span style={{ ...bulletStyle, lineHeight: 1.35 }}>{b}</span>
        </li>
      ))}
    </ol>
  );
}

export function SlideView({
  slide,
  layout,
  subject = '',
  eyebrow,
}: {
  slide: Slide;
  layout?: SlideLayout;
  subject?: string;
  /** Where in the hour this slide sits. Shown above every title. */
  eyebrow?: string;
}) {
  const prose = slide.elements.filter(isProse);
  const visuals = slide.elements.filter((e) => !isProse(e));
  const bullets = prose.flatMap((p) => p.bullets ?? []).filter((b) => b.trim());
  const chosen: SlideLayout = layout ?? inferLayout(slide.elements as { kind: string; bullets?: string[] }[]);

  /*
   * A sub-heading that restates the slide title is noise on a projector — the title is
   * already three times its size and directly above it. Exact matches are rare; what the
   * generator actually writes is "Key Determinants" under "Primary Determinants of Demand",
   * so a shared significant word is enough to drop it.
   */
  const heading = prose.find((p) => p.heading?.trim())?.heading?.trim();
  const significant = (t: string) =>
    new Set(
      t.toLowerCase().split(/[^a-z]+/).filter((w) => w.length > 3 && !STOP.has(w)),
    );
  const titleWords = significant(slide.title);
  const showHeading =
    !!heading && [...significant(heading)].every((w) => !titleWords.has(w));

  const frame: CSSProperties = {
    containerType: 'inline-size',
    width: '100%',
    height: '100%',
    display: 'grid',
    padding: SPACE.pad,
    boxSizing: 'border-box',
    // The deck's ground, and the second accent the diagrams reach for.
    color: DECK.ink,
    ['--c-accent-2' as string]: DECK.accent2,
  };

  if (chosen === 'statement' || chosen === 'question') {
    const line = bullets[0] ?? slide.title;
    const mark = chosen === 'question' ? 'question' : iconFor(line);
    return (
      <div style={{ ...frame, placeItems: 'center', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        {/* Decoration only, and behind everything. The slide reads the same without it. */}
        <Motif kind={motifFor(subject)} seed={slide.id} />
        {/*
          Wide enough that `text-wrap: balance` has something to work with. At 20ch a three-word
          title broke as "Determinants / of / Demand", stranding the preposition on its own line.
        */}
        <div style={{ maxWidth: 'min(30ch, 88%)', position: 'relative' }}>
          {mark ? (
            <span style={{ color: 'var(--c-brand, #5b46d6)', display: 'inline-flex', marginBottom: '.4em' }}>
              <Icon name={mark} size={52} />
            </span>
          ) : null}
          <div style={{ fontSize: TYPE.caption, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--c-ink-3)', fontWeight: 680, marginBottom: '.5em' }}>
            {chosen === 'question' ? 'Question' : eyebrow || slide.title}
          </div>
          <p style={{ fontSize: TYPE.display, lineHeight: 1.04, letterSpacing: '-.03em', fontWeight: 660, margin: 0, textWrap: 'balance' }}>
            {line}
          </p>
        </div>
      </div>
    );
  }

  if (chosen === 'visual' || (visuals.length && !bullets.length)) {
    return (
      <div style={{ ...frame, gridTemplateRows: 'auto 1fr', gap: SPACE.gap }}>
        <Masthead eyebrow={eyebrow} title={slide.title} small />
        <div style={{ display: 'grid', placeItems: 'center', minHeight: 0, gap: SPACE.gap }}>
          {visuals.map((el, i) => <SlideElementView key={i} el={el} />)}
        </div>
      </div>
    );
  }

  if (chosen === 'split' && visuals.length) {
    return (
      <div style={{ ...frame, gridTemplateRows: 'auto 1fr', gap: SPACE.gap }}>
        <Masthead eyebrow={eyebrow} title={slide.title} />
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 7fr)', gap: 'clamp(1.4rem, 3cqw, 3rem)', alignItems: 'center', minHeight: 0 }}>
          <div style={{ display: 'grid', gap: SPACE.gap, alignContent: 'center' }}>
            {showHeading ? <h3 style={{ fontSize: TYPE.lead, margin: 0, fontWeight: 640 }}>{heading}</h3> : null}
            {bullets.length ? <Bullets items={bullets} /> : null}
          </div>
          <div style={{ display: 'grid', placeItems: 'center', minHeight: 0 }}>
            {visuals.map((el, i) => <SlideElementView key={i} el={el} />)}
          </div>
        </div>
      </div>
    );
  }

  if (chosen === 'compare' && bullets.length >= 2) {
    const half = Math.ceil(bullets.length / 2);
    return (
      <div style={{ ...frame, gridTemplateRows: 'auto 1fr', gap: SPACE.gap }}>
        <Masthead eyebrow={eyebrow} title={slide.title} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(1.4rem, 3cqw, 3rem)', alignContent: 'center' }}>
          <Bullets items={bullets.slice(0, half)} />
          <Bullets items={bullets.slice(half)} />
        </div>
      </div>
    );
  }

  const body =
    chosen === 'grid' ? <CardGrid items={bullets} /> :
    chosen === 'steps' ? <Steps items={bullets} /> :
    <Bullets items={bullets} />;

  return (
    <div style={{ ...frame, gridTemplateRows: 'auto 1fr', gap: SPACE.gap }}>
      <Masthead eyebrow={eyebrow} title={slide.title} />
      <div style={{ display: 'grid', gap: SPACE.gap, alignContent: 'center', minHeight: 0 }}>
        {showHeading ? <h3 style={{ fontSize: TYPE.lead, margin: 0, fontWeight: 640, color: 'var(--c-ink-2)' }}>{heading}</h3> : null}
        {bullets.length ? body : null}
        {visuals.map((el, i) => <SlideElementView key={i} el={el} />)}
      </div>
    </div>
  );
}
